import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/cn";

/**
 * Top nav matching live code.genieacademy.com (legacy Menu.js / Menu.scss):
 * logo | Home · Create · My Projects | circular profile → Log Out
 */
export function AppMenu() {
  const { user, isStaff, displayName, photoURL, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "font-heading text-[1.35rem] sm:text-[1.75rem] px-3 py-2 text-ide-offwhite no-underline hover:text-white hover:no-underline",
      isActive && "text-white",
    );

  const avatarUrl = photoURL || "/photo.png";

  return (
    <header className="relative z-40 flex h-[90px] shrink-0 items-center bg-ide-dark px-3 sm:px-6">
      <NavLink to="/" className="mr-2 shrink-0 sm:mr-6" onClick={() => setNavOpen(false)}>
        {/* Same asset as live code.genieacademy.com navbar */}
        <img
          src="/genie-academy-logo.png"
          alt="iCode Genie"
          width={200}
          height={67}
          className="logo-img h-[70px] w-auto max-w-[200px] object-contain object-left sm:h-[85px] sm:max-w-[240px]"
          decoding="async"
        />
      </NavLink>

      <button
        type="button"
        className="order-3 ml-2 rounded border border-white/20 px-3 py-1 text-ide-offwhite xl:hidden"
        aria-label="Menu"
        onClick={() => setNavOpen((v) => !v)}
      >
        ☰
      </button>

      <nav
        className={cn(
          "absolute left-0 right-0 top-[90px] z-30 flex-col gap-1 border-b border-white/10 bg-ide-dark px-4 py-3 xl:static xl:z-auto xl:flex xl:flex-1 xl:flex-row xl:items-center xl:gap-1 xl:border-0 xl:p-0",
          navOpen ? "flex" : "hidden xl:flex",
        )}
      >
        <NavLink to="/" end className={linkClass} onClick={() => setNavOpen(false)}>
          Home
        </NavLink>
        <NavLink to="/create" className={linkClass} onClick={() => setNavOpen(false)}>
          Create
        </NavLink>
        <NavLink to="/projects" className={linkClass} onClick={() => setNavOpen(false)}>
          My Projects
        </NavLink>
        {isStaff && (
          <NavLink to="/admin" className={linkClass} onClick={() => setNavOpen(false)}>
            Admin
          </NavLink>
        )}
      </nav>

      <div className="relative ml-auto" ref={dropRef}>
        <button
          type="button"
          className="h-[46px] w-[46px] rounded-full border-2 border-white/20 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${avatarUrl})` }}
          aria-label="Account menu"
          onClick={() => setOpen((v) => !v)}
        />
        {open && (
          <div className="absolute right-0 top-full z-50 mt-2 min-w-[220px] rounded border border-white/15 bg-white py-1 text-left text-sm text-gray-900 shadow-lg">
            <div className="border-b border-gray-100 px-3 py-2 font-semibold">
              {displayName || "User"}
            </div>
            <div className="border-b border-gray-100 px-3 py-2 text-gray-600">
              {user?.email}
            </div>
            <button
              type="button"
              className="w-full px-3 py-2 text-left hover:bg-gray-100"
              onClick={() => {
                setOpen(false);
                void signOut();
              }}
            >
              Log Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
